import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { ElMessage } from 'element-plus'
import * as golfApi from '@/api/golf'
import { extractErrorMessage } from '@/helpers/error-parsers'
import type {
  GolfCarProfile,
  GolfCarAsset,
  GolfCarImage,
  GolfTariff,
  GolfTrip,
  GolfUser,
  GolfSettings,
  GolfPromoCode,
  CreateGolfCarProfilePayload,
  UpdateGolfCarProfilePayload,
  CreateGolfCarAssetPayload,
  UpdateGolfCarAssetPayload,
  CreateGolfCarImagePayload,
  CreateGolfTariffPayload,
  CreateGolfPromoCodePayload,
  UpdateGolfPromoCodePayload,
} from '@/api/golf'

export const useGolfStore = defineStore('golf', () => {
  const profiles = ref<GolfCarProfile[]>([])
  const currentProfile = ref<GolfCarProfile | null>(null)
  const profileAssets = ref<GolfCarAsset[]>([])
  const profileImages = ref<GolfCarImage[]>([])
  const profileTariffs = ref<GolfTariff[]>([])
  const profileTrips = ref<GolfTrip[]>([])
  const profileReserves = ref<GolfTrip[]>([])

  const trips = ref<GolfTrip[]>([])
  const users = ref<GolfUser[]>([])
  const settings = ref<GolfSettings | null>(null)
  const promoCodes = ref<GolfPromoCode[]>([])

  // Ensure promoCodes is always an array for table rendering
  const safePromoCodes = computed(() => {
    return Array.isArray(promoCodes.value) ? promoCodes.value : []
  })

  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const profileCount = computed(() => profiles.value.length)
  const activeProfiles = computed(() =>
    profiles.value.filter(p => p.status === 'active')
  )

  // ============ PROFILE ACTIONS ============

  const fetchProfiles = async (filters?: { status?: string }) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.getGolfCarProfiles(filters)
      profiles.value = response.data.data || []
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to fetch profiles')
      console.error('Error fetching profiles:', e)
    } finally {
      isLoading.value = false
    }
  }

  const fetchProfile = async (id: string) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.getGolfCarProfile(id)
      currentProfile.value = response.data.data || null
      return currentProfile.value
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to fetch profile')
      console.error('Error fetching profile:', e)
      return null
    } finally {
      isLoading.value = false
    }
  }

  const createProfile = async (payload: CreateGolfCarProfilePayload) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.createGolfCarProfile(payload)
      const newProfile = response.data.data
      if (newProfile) {
        profiles.value.push(newProfile)
        ElMessage.success('Golf car profile created successfully')
        return newProfile
      }
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to create profile')
      ElMessage.error({ message: error.value, duration: 6000, showClose: true })
      console.error('Error creating profile:', e)
    } finally {
      isLoading.value = false
    }
    return null
  }

  const updateProfile = async (
    id: string,
    payload: UpdateGolfCarProfilePayload
  ) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.updateGolfCarProfile(id, payload)
      const updated = response.data.data
      if (updated) {
        const index = profiles.value.findIndex(p => p.id === id)
        if (index !== -1) {
          profiles.value[index] = updated
        }
        if (currentProfile.value?.id === id) {
          currentProfile.value = updated
        }
        ElMessage.success('Profile updated successfully')
        return updated
      }
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to update profile')
      ElMessage.error({ message: error.value, duration: 6000, showClose: true })
      console.error('Error updating profile:', e)
    } finally {
      isLoading.value = false
    }
    return null
  }

  const blockProfile = async (id: string, comment: string) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.blockGolfCarProfile(id, comment)
      const updated = response.data.data
      if (updated) {
        const index = profiles.value.findIndex(p => p.id === id)
        if (index !== -1) {
          profiles.value[index] = updated
        }
        if (currentProfile.value?.id === id) {
          currentProfile.value = updated
        }
        ElMessage.success('Profile blocked successfully')
        return updated
      }
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to block profile')
      ElMessage.error({ message: error.value, duration: 6000, showClose: true })
      console.error('Error blocking profile:', e)
    } finally {
      isLoading.value = false
    }
    return null
  }

  const unblockProfile = async (id: string, comment?: string) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.unblockGolfCarProfile(id, comment)
      const updated = response.data.data
      if (updated) {
        const index = profiles.value.findIndex(p => p.id === id)
        if (index !== -1) {
          profiles.value[index] = updated
        }
        if (currentProfile.value?.id === id) {
          currentProfile.value = updated
        }
        ElMessage.success('Profile unblocked successfully')
        return updated
      }
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to unblock profile')
      ElMessage.error({ message: error.value, duration: 6000, showClose: true })
      console.error('Error unblocking profile:', e)
    } finally {
      isLoading.value = false
    }
    return null
  }

  // ============ ASSET ACTIONS ============

  const createAsset = async (
    profileId: string,
    payload: CreateGolfCarAssetPayload
  ) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.createGolfCarAsset(profileId, payload)
      const newAsset = response.data.data
      if (newAsset) {
        profileAssets.value.push(newAsset)
        ElMessage.success('Asset created successfully')
        return newAsset
      }
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to create asset')
      ElMessage.error({ message: error.value, duration: 6000, showClose: true })
      console.error('Error creating asset:', e)
    } finally {
      isLoading.value = false
    }
    return null
  }

  const updateAsset = async (
    id: string,
    payload: UpdateGolfCarAssetPayload
  ) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.updateGolfCarAsset(id, payload)
      const updated = response.data.data
      if (updated) {
        const index = profileAssets.value.findIndex(a => a.id === id)
        if (index !== -1) {
          profileAssets.value[index] = updated
        }
        ElMessage.success('Asset updated successfully')
        return updated
      }
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to update asset')
      ElMessage.error({ message: error.value, duration: 6000, showClose: true })
      console.error('Error updating asset:', e)
    } finally {
      isLoading.value = false
    }
    return null
  }

  const blockAsset = async (id: string, comment: string) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.blockGolfCarAsset(id, { comment })
      const updated = response.data.data
      if (updated) {
        const index = profileAssets.value.findIndex(a => a.id === id)
        if (index !== -1) {
          profileAssets.value[index] = updated
        }
        ElMessage.success('Asset blocked successfully')
        return updated
      }
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to block asset')
      ElMessage.error({ message: error.value, duration: 6000, showClose: true })
      console.error('Error blocking asset:', e)
    } finally {
      isLoading.value = false
    }
    return null
  }

  const unblockAsset = async (id: string, comment?: string) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.unblockGolfCarAsset(id, comment)
      const updated = response.data.data
      if (updated) {
        const index = profileAssets.value.findIndex(a => a.id === id)
        if (index !== -1) {
          profileAssets.value[index] = updated
        }
        ElMessage.success('Asset unblocked successfully')
        return updated
      }
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to unblock asset')
      ElMessage.error({ message: error.value, duration: 6000, showClose: true })
      console.error('Error unblocking asset:', e)
    } finally {
      isLoading.value = false
    }
    return null
  }

  // ============ IMAGE ACTIONS ============

  const createImage = async (
    profileId: string,
    payload: FormData
  ) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.createGolfCarImage(profileId, payload)
      const newImage = response.data.data
      if (newImage) {
        profileImages.value.push(newImage)
        ElMessage.success('Image added successfully')
        return newImage
      }
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to add image')
      ElMessage.error({ message: error.value, duration: 6000, showClose: true })
      console.error('Error adding image:', e)
    } finally {
      isLoading.value = false
    }
    return null
  }

  const deleteImage = async (imageId: string) => {
    isLoading.value = true
    error.value = null
    try {
      await golfApi.deleteGolfCarImage(imageId)
      profileImages.value = profileImages.value.filter(
        img => img.id !== imageId
      )
      ElMessage.success('Image deleted successfully')
      return true
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to delete image')
      ElMessage.error({ message: error.value, duration: 6000, showClose: true })
      console.error('Error deleting image:', e)
    } finally {
      isLoading.value = false
    }
    return false
  }

  // ============ TARIFF ACTIONS ============

  const fetchTariffs = async () => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.getGolfTariffs()
      profileTariffs.value = response.data.data || []
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to fetch tariffs')
      console.error('Error fetching tariffs:', e)
    } finally {
      isLoading.value = false
    }
  }

  const createTariff = async (payload: CreateGolfTariffPayload) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.createGolfTariff(payload)
      const newTariff = response.data.data
      if (newTariff) {
        profileTariffs.value.push(newTariff)
        ElMessage.success('Tariff created successfully')
        return newTariff
      }
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to create tariff')
      ElMessage.error({ message: error.value, duration: 6000, showClose: true })
      console.error('Error creating tariff:', e)
    } finally {
      isLoading.value = false
    }
    return null
  }

  const updateTariff = async (
    id: string,
    payload: Partial<CreateGolfTariffPayload>
  ) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.updateGolfTariff(id, payload)
      const updated = response.data.data
      if (updated) {
        const index = profileTariffs.value.findIndex(t => t.id === id)
        if (index !== -1) {
          profileTariffs.value[index] = updated
        }
        ElMessage.success('Tariff updated successfully')
        return updated
      }
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to update tariff')
      ElMessage.error({ message: error.value, duration: 6000, showClose: true })
      console.error('Error updating tariff:', e)
    } finally {
      isLoading.value = false
    }
    return null
  }

  const attachTariff = async (profileId: string, tariffId: string) => {
    isLoading.value = true
    error.value = null
    try {
      // TODO: Implement attach tariff logic when API is available
      ElMessage.success('Tariff attached to profile')
      return true
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to attach tariff')
      ElMessage.error({ message: error.value, duration: 6000, showClose: true })
      console.error('Error attaching tariff:', e)
    } finally {
      isLoading.value = false
    }
    return false
  }

  const detachTariff = async (profileId: string, tariffId: string) => {
    isLoading.value = true
    error.value = null
    try {
      // TODO: Implement detach tariff logic when API is available
      profileTariffs.value = profileTariffs.value.filter(
        t => t.id !== tariffId
      )
      ElMessage.success('Tariff detached from profile')
      return true
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to detach tariff')
      ElMessage.error({ message: error.value, duration: 6000, showClose: true })
      console.error('Error detaching tariff:', e)
    } finally {
      isLoading.value = false
    }
    return false
  }

  // ============ TRIP ACTIONS ============

  const fetchTrips = async (filters?: {
    status?: string
    pricing_type?: string
    payment_method?: string
  }) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.getGolfTrips(filters)
      trips.value = response.data.data || []
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to fetch trips')
      console.error('Error fetching trips:', e)
    } finally {
      isLoading.value = false
    }
  }

  const fetchTrip = async (id: string) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.getGolfTrip(id)
      return response.data.data || null
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to fetch trip')
      console.error('Error fetching trip:', e)
      return null
    } finally {
      isLoading.value = false
    }
  }

  const approveTrip = async (id: string, assetId?: string) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.approveGolfTrip(id)
      const updated = response.data.data
      if (updated) {
        const index = trips.value.findIndex(t => t.id === id)
        if (index !== -1) {
          trips.value[index] = updated
        }
        ElMessage.success('Trip approved successfully')
        return updated
      }
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to approve trip')
      ElMessage.error({ message: error.value, duration: 6000, showClose: true })
      console.error('Error approving trip:', e)
    } finally {
      isLoading.value = false
    }
    return null
  }

  const declineTrip = async (id: string, comment?: string) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.declineGolfTrip(id, comment)
      const updated = response.data.data
      if (updated) {
        const index = trips.value.findIndex(t => t.id === id)
        if (index !== -1) {
          trips.value[index] = updated
        }
        ElMessage.success('Trip declined successfully')
        return updated
      }
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to decline trip')
      ElMessage.error({ message: error.value, duration: 6000, showClose: true })
      console.error('Error declining trip:', e)
    } finally {
      isLoading.value = false
    }
    return null
  }

  // ============ USER ACTIONS ============

  const fetchUsers = async (filters?: {
    status_golf?: string
    q?: string
  }) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.getGolfUsers(filters)
      users.value = response.data.data || []
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to fetch users')
      console.error('Error fetching users:', e)
    } finally {
      isLoading.value = false
    }
  }

  const fetchUser = async (id: string) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.getGolfUser(id)
      return response.data.data || null
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to fetch user')
      console.error('Error fetching user:', e)
      return null
    } finally {
      isLoading.value = false
    }
  }

  const updateUserStatus = async (
    id: string,
    statusGolf: 'active' | 'inactive'
  ) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.updateGolfUserStatus(id, statusGolf)
      const updated = response.data.data
      if (updated) {
        const index = users.value.findIndex(u => u.id === id)
        if (index !== -1) {
          users.value[index] = updated
        }
        ElMessage.success(
          `User access ${statusGolf === 'active' ? 'granted' : 'revoked'}`
        )
        return updated
      }
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to update user status')
      ElMessage.error({ message: error.value, duration: 6000, showClose: true })
      console.error('Error updating user status:', e)
    } finally {
      isLoading.value = false
    }
    return null
  }

  // ============ SETTINGS ACTIONS ============

  const fetchSettings = async () => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.getGolfSettings()
      settings.value = response.data.data || null
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to fetch settings')
      console.error('Error fetching settings:', e)
    } finally {
      isLoading.value = false
    }
  }

  const updateSettings = async (payload: Partial<GolfSettings>) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.updateGolfSettingsBulk(payload)
      settings.value = response.data.data || null
      ElMessage.success('Settings updated successfully')
      return settings.value
    } catch (e: unknown) {
      error.value = extractErrorMessage(e, 'Failed to update settings')
      ElMessage.error({ message: error.value, duration: 6000, showClose: true })
      console.error('Error updating settings:', e)
    } finally {
      isLoading.value = false
    }
    return null
  }

  // ============ PROMO CODE ACTIONS ============

  const fetchPromoCodes = async () => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.getGolfPromoCodes()
      promoCodes.value = (response.data?.data?.data || []) as unknown as GolfPromoCode[]
    } catch (e: unknown) {
      error.value = extractErrorMessage(e, 'Failed to fetch promo codes')
      console.error('Error fetching promo codes:', e)
      promoCodes.value = []
    } finally {
      isLoading.value = false
    }
  }

  const createPromoCode = async (payload: CreateGolfPromoCodePayload) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.createGolfPromoCode(payload)
      const newPromoCode = response.data.data
      if (newPromoCode) {
        promoCodes.value.push(newPromoCode)
        ElMessage.success('Promo code created successfully')
        return newPromoCode
      }
    } catch (e: unknown) {
      error.value = extractErrorMessage(e, 'Failed to create promo code')
      ElMessage.error({ message: error.value, duration: 6000, showClose: true })
      console.error('Error creating promo code:', e)
    } finally {
      isLoading.value = false
    }
    return null
  }

  const updatePromoCode = async (
    id: string,
    payload: UpdateGolfPromoCodePayload
  ) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.updateGolfPromoCode(id, payload)
      const updated = response.data.data.data as unknown as GolfPromoCode
      if (updated) {
        const index = promoCodes.value.findIndex(p => p.id === id)
        if (index !== -1) {
          promoCodes.value[index] = updated
        }
        ElMessage.success('Promo code updated successfully')
        return updated
      }
    } catch (e: unknown) {
      error.value = extractErrorMessage(e, 'Failed to update promo code')
      ElMessage.error({ message: error.value, duration: 6000, showClose: true })
      console.error('Error updating promo code:', e)
    } finally {
      isLoading.value = false
    }
    return null
  }

  const deletePromoCode = async (id: string) => {
    isLoading.value = true
    error.value = null
    try {
      await golfApi.deleteGolfPromoCode(id)
      const target = promoCodes.value.find(p => p.id === id)
      if (target) {
        target.status = 'stopped'
        target.is_active = false
      }
      ElMessage.success('Promo code stopped successfully')
      return true
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to stop promo code')
      ElMessage.error({ message: error.value, duration: 6000, showClose: true })
      console.error('Error stopping promo code:', e)
    } finally {
      isLoading.value = false
    }
    return false
  }

  const deactivatePromoCode = async (id: string) => {
    isLoading.value = true
    error.value = null
    try {
      await golfApi.deactivateGolfPromoCode(id)
      const index = promoCodes.value.findIndex(p => p.id === id)
      if (index !== -1) {
        promoCodes.value[index].status = 'stopped'
        promoCodes.value[index].is_active = false
      }
      ElMessage.success('Promo code stopped successfully')
      return true
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to stop promo code')
      ElMessage.error({ message: error.value, duration: 6000, showClose: true })
      console.error('Error stopping promo code:', e)
    } finally {
      isLoading.value = false
    }
    return false
  }

  const bulkCreatePromoCodes = async (
    payload: golfApi.BulkCreateGolfPromoCodePayload
  ) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await golfApi.bulkCreateGolfPromoCodes(payload)
      ElMessage.success(`Successfully generated ${response.data.data.count} promo codes`)
      await fetchPromoCodes()
      return response.data.data
    } catch (e: any) {
      error.value = extractErrorMessage(e, 'Failed to bulk generate promo codes')
      ElMessage.error({ message: error.value, duration: 6000, showClose: true })
      console.error('Error bulk generating promo codes:', e)
    } finally {
      isLoading.value = false
    }
    return null
  }

  const pausePromoCode = async (id: string) => {
    try {
      const response = await golfApi.pauseGolfPromoCode(id)
      const updated = response.data.data
      const index = promoCodes.value.findIndex(p => p.id === id)
      if (index !== -1 && updated) {
        promoCodes.value[index] = updated
      }
      ElMessage.success('Campaign paused')
      return updated
    } catch (e: any) {
      const msg = extractErrorMessage(e, 'Failed to pause campaign')
      ElMessage.error({ message: msg, duration: 6000, showClose: true })
      return null
    }
  }

  const resumePromoCode = async (id: string) => {
    try {
      const response = await golfApi.resumeGolfPromoCode(id)
      const updated = response.data.data
      const index = promoCodes.value.findIndex(p => p.id === id)
      if (index !== -1 && updated) {
        promoCodes.value[index] = updated
      }
      ElMessage.success('Campaign resumed')
      return updated
    } catch (e: any) {
      const msg = extractErrorMessage(e, 'Failed to resume campaign')
      ElMessage.error({ message: msg, duration: 6000, showClose: true })
      return null
    }
  }

  const stopPromoCode = async (id: string) => {
    try {
      const response = await golfApi.stopGolfPromoCode(id)
      const updated = response.data.data
      const index = promoCodes.value.findIndex(p => p.id === id)
      if (index !== -1 && updated) {
        promoCodes.value[index] = updated
      }
      ElMessage.success('Campaign stopped')
      return updated
    } catch (e: any) {
      const msg = extractErrorMessage(e, 'Failed to stop campaign')
      ElMessage.error({ message: msg, duration: 6000, showClose: true })
      return null
    }
  }

  const exportPromoCodesCsv = async () => {
    try {
      const response = await golfApi.exportGolfPromoCodesCsv()
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `golf_promo_codes_${Date.now()}.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      ElMessage.success('CSV exported successfully')
    } catch (e: any) {
      const msg = extractErrorMessage(e, 'Failed to export CSV')
      ElMessage.error({ message: msg, duration: 6000, showClose: true })
    }
  }

  const fetchPromoCodeStats = async () => {
    try {
      const response = await golfApi.getGolfPromoCodeStats()
      return response.data.data
    } catch (e: any) {
      console.error('Error fetching promo code stats:', e)
      return null
    }
  }

  // ============ RESET ============

  const resetError = () => {
    error.value = null
  }

  const clearCurrentProfile = () => {
    currentProfile.value = null
    profileAssets.value = []
    profileImages.value = []
    profileTariffs.value = []
    profileTrips.value = []
    profileReserves.value = []
  }

  return {
    profiles,
    currentProfile,
    profileAssets,
    profileImages,
    profileTariffs,
    profileTrips,
    profileReserves,
    trips,
    users,
    settings,
    promoCodes,
    safePromoCodes,
    isLoading,
    error,

    profileCount,
    activeProfiles,

    fetchProfiles,
    fetchProfile,
    createProfile,
    updateProfile,
    blockProfile,
    unblockProfile,
    createAsset,
    updateAsset,
    blockAsset,
    unblockAsset,
    createImage,
    deleteImage,
    fetchTariffs,
    createTariff,
    updateTariff,
    attachTariff,
    detachTariff,
    fetchTrips,
    fetchTrip,
    approveTrip,
    declineTrip,
    fetchUsers,
    fetchUser,
    updateUserStatus,
    fetchSettings,
    updateSettings,
    fetchPromoCodes,
    createPromoCode,
    updatePromoCode,
    deletePromoCode,
    deactivatePromoCode,
    bulkCreatePromoCodes,
    pausePromoCode,
    resumePromoCode,
    stopPromoCode,
    exportPromoCodesCsv,
    fetchPromoCodeStats,
    resetError,
    clearCurrentProfile,
  }
})
